import { SortableHeader, SortableHeadersGroup, ConfirmationDialog, ConfirmationDialogRef } from 'components/Shared';
import { SortingParams } from 'contracts/Common';
import type { PaginatedDockReservedInterval } from 'contracts/DockReservedIntervals';
import { AppDispatch, RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteDockReservedIntervalActions as DeleteActions } from 'store/ducks/dockReservedIntervals';
import React, { useCallback, useRef } from 'react';
import { Formatter } from 'utils';
import * as S from './styles';

interface Props {
  intervals: PaginatedDockReservedInterval[];
  onSort?: (sort: SortingParams) => void;
  currentSort: SortingParams;
  onDeleteSuccess?: () => void;
}

interface ItemProps {
  interval: PaginatedDockReservedInterval;
  onDelete: (id: number | string) => void;
  deletingId?: number | string | null;
}

export const IntervalsList: React.FC<Props> = ({
  intervals,
  onSort,
  currentSort,
  onDeleteSuccess,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const dialogRef = useRef<ConfirmationDialogRef>(null);

  const { loading: deletingInterval, id: deletingId } = useSelector(
    (state: RootState) => state.deleteDockReservedInterval
  );

  const handleDelete = useCallback(
    async (id: number | string) => {
      const confirmed = await dialogRef.current?.openDialog({
        title: 'Deseja remover o bloqueio de horário?',
        message: 'Esta ação não poderá ser desfeita após confirmada.',
        confirmButtonMood: 'outlinedDanger',
      });
      if (confirmed) {
        dispatch(DeleteActions.request(id, onDeleteSuccess));
      }
    },
    [dispatch, onDeleteSuccess]
  );

  const Item = useCallback(({ interval, onDelete, deletingId }: ItemProps): JSX.Element => {
    const { id, startDate, endDate, startTime, endTime, dock } = interval;

    return (
      <S.ListItem>
        <S.Column>{id}</S.Column>
        <S.Column>{dock.warehouse.tradeName}</S.Column>
        <S.Column>{dock.name}</S.Column>
        <S.Column>
          {Formatter.date(`${startDate.split('T')[0]} ${startTime}`)}
        </S.Column>
        <S.Column>
          {Formatter.date(`${endDate.split('T')[0]} ${endTime}`)}
        </S.Column>
        <S.Column>{startTime}</S.Column>
        <S.Column>{endTime}</S.Column>
        <S.ActionsColumn>
           <S.ActionButton
              type="button"
              mood="danger"
              disabled={deletingId === id}
              onClick={() => onDelete(id)}
            >
              {deletingId === id ? <S.ActivityIndicator /> : <S.TrashIcon />}
            </S.ActionButton>
          <S.LinkActionButton
            to={`/configuracoes/bloqueio-de-horarios/${id}/editar`}
          >
            <S.EditIcon />
          </S.LinkActionButton>
        </S.ActionsColumn>
      </S.ListItem>
    );
  }, []);

  return (
   <>
    <ConfirmationDialog ref={dialogRef} />
    <S.List>
      <S.ListHeader>
        <SortableHeadersGroup onSort={onSort} currentSort={currentSort}>
          <SortableHeader column="id" label="ID" />
          <SortableHeader column="warehouseTradeName" label="ARMAZÉM" />
          <SortableHeader column="dockName" label="DOCA" />
          <SortableHeader column="startDate" label="DATA INÍCIO" />
          <SortableHeader column="endDate" label="DATA TÉRMINO" />
          <SortableHeader column="startTime" label="HORA INÍCIO" />
          <SortableHeader column="endTime" label="HORA TÉRMINO" />
        </SortableHeadersGroup>
      </S.ListHeader>
      {intervals.map((interval) => (
        <Item key={interval.id} interval={interval} onDelete={handleDelete} deletingId={deletingId} />
      ))}
    </S.List>
    </>
  );
};
